import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import ReactApexChart from 'react-apexcharts';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
const ProfitLoss = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]
    });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    useEffect(() => {
        fetchData();
    }, [dateRange]);
    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await apiGet(
                `/admin/profit-loss-summary?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`
            );
            if (result.success) {
                // Ensure data has required structure with defaults
                const safeData = {
                    summary: {
                        totalRevenue: result.data?.summary?.totalRevenue || 0,
                        totalExpenses: result.data?.summary?.totalExpenses || 0,
                        netProfit: result.data?.summary?.netProfit || 0,
                        margin: result.data?.summary?.margin || '0.00'
                    },
                    chartData: result.data?.chartData || [],
                    breakdown: {
                        revenue: result.data?.breakdown?.revenue || [{ name: 'No Data', value: 0 }],
                        expenses: result.data?.breakdown?.expenses || [{ name: 'No Data', value: 0 }]
                    }
                };
                setData(safeData);
            } else {
                setError(result.message || 'Failed to fetch data');
            }
        } catch (error) {
            console.error('Error fetching P&L data:', error);
            setError(error.message || 'Failed to connect to server');
        }
        setLoading(false);
    };
    const colors = {
        primary: '#2D5016',
        secondary: '#4a7c2a',
        accent: '#6ba83e',
        danger: '#ef4444',
        success: '#10b981',
        warning: '#f59e0b',
        textDark: '#1a1a1a',
        textLight: '#6b7280',
        white: '#ffffff',
        lightGray: '#f8faf9',
        border: '#e5e7eb'
    };
    const styles = {
        pageHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
            flexWrap: 'wrap',
            gap: '16px'
        },
        title: {
            fontSize: isMobile ? '24px' : '28px',
            fontWeight: '800',
            color: colors.primary,
            margin: 0
        },
        card: {
            background: colors.white,
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            marginBottom: '24px'
        },
        statsGrid: {
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
            gap: '24px',
            marginBottom: '24px'
        },
        statCard: {
            background: colors.white,
            padding: '20px',
            borderRadius: '16px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            borderLeft: '4px solid',
            position: 'relative',
            overflow: 'hidden'
        },
        statLabel: {
            fontSize: '14px',
            color: colors.textLight,
            marginBottom: '8px',
            fontWeight: '600'
        },
        statValue: {
            fontSize: '28px',
            fontWeight: '800',
            marginBottom: '4px'
        },
        chartsGrid: {
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
            gap: '24px',
            marginBottom: '24px'
        },
        sectionTitle: {
            fontSize: '18px',
            fontWeight: '700',
            color: colors.textDark,
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        dateInput: {
            padding: '10px 16px',
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            fontSize: '14px',
            outline: 'none',
            color: colors.textDark,
            background: colors.white,
            cursor: 'pointer'
        },
        table: {
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: '0'
        },
        th: {
            textAlign: 'left',
            padding: '16px',
            background: colors.lightGray,
            color: colors.textLight,
            fontWeight: '600',
            fontSize: '13px',
            borderBottom: `1px solid ${colors.border}`,
            textTransform: 'uppercase'
        },
        td: {
            padding: '16px',
            borderBottom: `1px solid ${colors.border}`,
            color: colors.textDark,
            fontSize: '14px'
        },
        errorBox: {
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            color: '#dc2626'
        },
        retryButton: {
            background: colors.primary,
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            cursor: 'pointer',
            marginTop: '10px',
            fontSize: '14px'
        }
    };
    const RevenueChart = {
        series: [
            {
                name: 'Revenue',
                data: data?.chartData?.map(d => d.revenue) || []
            },
            {
                name: 'Expenses',
                data: data?.chartData?.map(d => d.expenses) || []
            }
        ],
        options: {
            chart: {
                type: 'area',
                height: 350,
                toolbar: { show: false },
                fontFamily: 'Inter, sans-serif'
            },
            colors: [colors.success, colors.danger],
            dataLabels: { enabled: false },
            stroke: { curve: 'smooth', width: 2 },
            xaxis: {
                categories: data?.chartData?.map(d => {
                    try {
                        return new Date(d.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
                    } catch {
                        return d.date;
                    }
                }) || [],
                labels: { style: { colors: colors.textLight } }
            },
            yaxis: {
                labels: {
                    style: { colors: colors.textLight },
                    formatter: (value) => `₹${(value / 1000).toFixed(0)}k`
                }
            },
            fill: {
                type: 'gradient',
                gradient: {
                    shadeIntensity: 1,
                    opacityFrom: 0.7,
                    opacityTo: 0.2
                }
            },
            tooltip: {
                y: {
                    formatter: (value) => `₹${value?.toLocaleString('en-IN') || 0}`
                }
            },
            noData: {
                text: 'No data available for selected period',
                style: {
                    color: colors.textLight
                }
            }
        }
    };
    const CategoryChart = (breakdown, chartColors) => ({
        series: breakdown?.map(item => item.value) || [0],
        options: {
            chart: {
                type: 'donut',
                fontFamily: 'Inter, sans-serif'
            },
            labels: breakdown?.map(item => item.name) || ['No Data'],
            colors: chartColors || [colors.success, colors.primary, colors.accent],
            legend: {
                position: 'bottom',
                labels: { colors: colors.textLight }
            },
            dataLabels: { enabled: false },
            plotOptions: {
                pie: {
                    donut: {
                        size: '75%',
                        labels: {
                            show: true,
                            total: {
                                show: true,
                                label: 'Total',
                                formatter: (w) => {
                                    const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                                    return `₹${(total / 1000).toFixed(1)}k`;
                                }
                            }
                        }
                    }
                }
            },
            noData: {
                text: 'No data available',
                style: {
                    color: colors.textLight
                }
            }
        }
    });
    if (loading && !data) {
        return (
            <AdminLayout>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                    <div style={{ fontSize: '24px', color: colors.primary }}>Loading Financial Data...</div>
                </div>
            </AdminLayout>
        );
    }
    return (
        <AdminLayout>
            <div style={styles.pageHeader}>
                <div>
                    <h1 style={styles.title}>Profit & Loss Statement</h1>
                    <p style={{ margin: '4px 0 0', color: colors.textLight }}>Financial performance overview</p>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                    <input
                        type="date"
                        value={dateRange.startDate}
                        onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                        style={styles.dateInput}
                    />
                    <span style={{ display: 'flex', alignItems: 'center', color: colors.textLight }}>to</span>
                    <input
                        type="date"
                        value={dateRange.endDate}
                        onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                        style={styles.dateInput}
                    />
                </div>
            </div>
            {error && (
                <div style={styles.errorBox}>
                    <strong>Error:</strong> {error}
                    <br />
                    <button style={styles.retryButton} onClick={fetchData}>
                        Retry
                    </button>
                </div>
            )}
            {data && (
                <>
                    <div style={styles.statsGrid}>
                        <div style={{ ...styles.statCard, borderLeftColor: colors.success }}>
                            <div style={styles.statLabel}>Total Revenue</div>
                            <div style={{ ...styles.statValue, color: colors.success }}>
                                ₹{(data.summary.totalRevenue || 0).toLocaleString('en-IN')}
                            </div>
                        </div>
                        <div style={{ ...styles.statCard, borderLeftColor: colors.danger }}>
                            <div style={styles.statLabel}>Total Expenses</div>
                            <div style={{ ...styles.statValue, color: colors.danger }}>
                                ₹{(data.summary.totalExpenses || 0).toLocaleString('en-IN')}
                            </div>
                        </div>
                        <div style={{ ...styles.statCard, borderLeftColor: (data.summary.netProfit || 0) >= 0 ? colors.primary : colors.warning }}>
                            <div style={styles.statLabel}>Net Profit/Loss</div>
                            <div style={{
                                ...styles.statValue,
                                color: (data.summary.netProfit || 0) >= 0 ? colors.primary : colors.warning
                            }}>
                                ₹{(data.summary.netProfit || 0).toLocaleString('en-IN')}
                            </div>
                        </div>
                        <div style={{ ...styles.statCard, borderLeftColor: colors.accent }}>
                            <div style={styles.statLabel}>Net Margin</div>
                            <div style={{ ...styles.statValue, color: colors.accent }}>
                                {data.summary.margin || 0}%
                            </div>
                        </div>
                    </div>
                    <div style={styles.chartsGrid}>
                        <div style={styles.card}>
                            <div style={styles.sectionTitle}>
                                <span>📈</span> Revenue vs Expenses Trend
                            </div>
                            {data.chartData && data.chartData.length > 0 ? (
                                <ReactApexChart
                                    options={RevenueChart.options}
                                    series={RevenueChart.series}
                                    type="area"
                                    height={350}
                                />
                            ) : (
                                <div style={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.textLight }}>
                                    No trend data available for selected period
                                </div>
                            )}
                        </div>
                        <div style={styles.card}>
                            <div style={styles.sectionTitle}>
                                <span>💰</span> Revenue Breakdown
                            </div>
                            {data.breakdown.revenue && data.breakdown.revenue.length > 0 && data.breakdown.revenue[0].value > 0 ? (
                                <ReactApexChart
                                    options={CategoryChart(data.breakdown.revenue, [colors.success, colors.primary, colors.accent]).options}
                                    series={CategoryChart(data.breakdown.revenue).series}
                                    type="donut"
                                    height={350}
                                />
                            ) : (
                                <div style={{ height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.textLight }}>
                                    No revenue data available
                                </div>
                            )}
                        </div>
                    </div>
                    <div style={styles.chartsGrid}>
                        <div style={styles.card}>
                            <div style={styles.sectionTitle}>
                                <span>💸</span> Expense Breakdown
                            </div>
                            <div style={{ height: '350px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {data.breakdown.expenses && data.breakdown.expenses.length > 0 && data.breakdown.expenses[0].value > 0 ? (
                                    <ReactApexChart
                                        options={CategoryChart(data.breakdown.expenses, [colors.danger, colors.warning, '#f472b6', '#a78bfa']).options}
                                        series={CategoryChart(data.breakdown.expenses).series}
                                        type="donut"
                                        height={320}
                                        width={400}
                                    />
                                ) : (
                                    <div style={{ color: colors.textLight }}>
                                        No expense data available
                                    </div>
                                )}
                            </div>
                        </div>
                        <div style={styles.card}>
                            <div style={styles.sectionTitle}>
                                <span>📑</span> Detailed Statement
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>Category</th>
                                            <th style={{ ...styles.th, textAlign: 'right' }}>Amount</th>
                                            <th style={{ ...styles.th, textAlign: 'right' }}>% of Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td style={{ ...styles.td, fontWeight: '700', color: colors.success }} colSpan={3}>REVENUE</td>
                                        </tr>
                                        {data.breakdown.revenue && data.breakdown.revenue.length > 0 ? (
                                            data.breakdown.revenue.map((item, i) => (
                                                <tr key={i}>
                                                    <td style={{ ...styles.td, paddingLeft: '32px' }}>{item.name}</td>
                                                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: '500' }}>
                                                        ₹{(item.value || 0).toLocaleString('en-IN')}
                                                    </td>
                                                    <td style={{ ...styles.td, textAlign: 'right', color: colors.textLight }}>
                                                        {data.summary.totalRevenue > 0 ? ((item.value / data.summary.totalRevenue) * 100).toFixed(1) : 0}%
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td style={{ ...styles.td, paddingLeft: '32px', color: colors.textLight }} colSpan={3}>No revenue data</td>
                                            </tr>
                                        )}
                                        <tr>
                                            <td style={{ ...styles.td, fontWeight: '700', color: colors.danger, paddingTop: '24px' }} colSpan={3}>EXPENSES</td>
                                        </tr>
                                        {data.breakdown.expenses && data.breakdown.expenses.length > 0 ? (
                                            data.breakdown.expenses.map((item, i) => (
                                                <tr key={i}>
                                                    <td style={{ ...styles.td, paddingLeft: '32px' }}>{item.name}</td>
                                                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: '500' }}>
                                                        ₹{(item.value || 0).toLocaleString('en-IN')}
                                                    </td>
                                                    <td style={{ ...styles.td, textAlign: 'right', color: colors.textLight }}>
                                                        {data.summary.totalExpenses > 0 ? ((item.value / data.summary.totalExpenses) * 100).toFixed(1) : 0}%
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td style={{ ...styles.td, paddingLeft: '32px', color: colors.textLight }} colSpan={3}>No expense data</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </AdminLayout>
    );
};
export default ProfitLoss;
